import React, { useState, useEffect } from 'react'
import * as Yup from 'yup'
import { COOKIES } from 'utils/constants'
import { useForm } from 'react-hook-form/dist/react-hook-form.ie11'
import useLeadInfo from '@/modern/actions/useLeadInfo'
import useModal from '@/modern/actions/useModal'
import useCalendar from '@/modern/hooks/useCalendar'
import useLead from '@/modern/hooks/useLead'
import { handleFormCookie } from 'utils/utils'
import ModalContainer from '@/modern/components/ModalContainer'
import Icon from '@/modern/components/Icon'
import { cookieCategoryIsActive } from '@/modern/utils/cookies'
import { useCookieNoticeState } from '@/store/context/cookie-notice-context'
import { leadAPI } from '@/modern/services/api'
import Form from './Form'
import Calendar from './Calendar'
import Hours from './Hours'
import {
  VideoChamadaModal,
  MainText,
  FlexContainer,
  IconButton,
  FormInfo,
  Button,
} from './style'
import { useTracking } from '@/modern/tracking/global/hooks/useTracking'
import createTrackingData from '@/modern/tracking/global/helpers/createTrackingData'
import { DATALAYER_PAGES } from '@/modern/tracking/pages'
import { getQueryParameter, onClientSide, sendDispatchEvent } from '@/utils/utils'
import useAuth from '@/modern/actions/useAuth'

export default ({ ad, idGuid }) => {
  const leadSchema = Yup.object().shape({
    fullName: Yup.string().min(3),
    email: Yup.string().email().required(),
    numberPhone: Yup.string().min(14),
  })

  const { cookies } = useCookieNoticeState()
  const isActive = cookieCategoryIsActive(cookies, COOKIES.FORM_DATA)

  const { closeModal, modal } = useModal('videoChamada')
  const { openModal } = useModal('videoChamadaSucesso')
  const { setLeadInfo } = useLeadInfo('videoChamadaDate')
  const { getJSONOfDays, getRoundedNowDate, formatTime } = useCalendar()
  const formHandles = useForm({
    mode: 'onBlur',
    validationSchema: leadSchema,
    submitFocusError: false,
  })
  const { formatAdobeGuid, formatChannelForLead, formatMessage } = useLead()

  const { dispatchAnalyticsEvent } = useTracking()

  const { userIsAuth } = useAuth()

  const [isActiveForm, setIsActiveForm] = useState(false)
  const [isActiveCalendar, setisActiveCalendar] = useState(false)
  const [isActiveHours, setIsActiveHours] = useState(false)
  const [dayActive, setDayActive] = useState(0)
  const [hourActive, setHourActive] = useState(getRoundedNowDate())
  const [loading, setLoading] = useState(false)

  const allDays = getJSONOfDays()

  useEffect(() => {
    if (modal && onClientSide()) {
      const { search, href } = window.location

      const customObjDatalayer = {
        site: {
          brandOrigin: getQueryParameter(search, 'origem') || 'webmotors',
        },
        user: {
          loginStatus: userIsAuth ? 1 : 2,
        },
        page: {
          url: href,
          trackingCode: {
            idcmp: getQueryParameter(search, 'idcmp'),
            idcmpint: getQueryParameter(search, 'idcmpint'),
            icid: getQueryParameter(search, 'icid'),
            lkid: getQueryParameter(search, 'lkid'),
          },
        },
      }

      const trackingData = createTrackingData(
        'customPageView',
        DATALAYER_PAGES.videoCallScheduling,
        customObjDatalayer
      )

      dispatchAnalyticsEvent(trackingData)
    }
  }, [ad, dispatchAnalyticsEvent, modal, userIsAuth])

  const submitForm = data => {
    const channel = formatChannelForLead()
    const adobeGuid = formatAdobeGuid()
    const day = allDays[dayActive]
    const { formattedHour, formattedMinutes } = formatTime(
      hourActive.getHours(),
      hourActive.getMinutes()
    )
    const leadDateInfo = {
      day,
      formattedTime: { formattedHour, formattedMinutes },
    }
    const message = formatMessage(
      data.carDelivery,
      leadDateInfo,
      (ad.Type === 'car' && 'do carro') || 'da moto'
    )

    const lead = {
      IdAnuncio: ad.UniqueId,
      TipoVeiculo: ad.Type,
      Nome: data.fullName,
      Email: data.email,
      Telefone: data.numberPhone,
      Mensagem: message,
      TipoAnuncio: ad.ListingType === 'U' ? 'usados' : 'novos',
      Origem: 'D',
      IdGuid: idGuid,
      QueroReceberPesquisa: data.newsletter,
      Canal: channel,
      AdobeGuid: adobeGuid,
    }
    const newObjDataLayer = {
      ...objDataLayer,
      conversion: {
        ...objDataLayer.conversion,
        videocall: { optin: { carDelivery: '' } },
      },
    }
    newObjDataLayer.conversion.videocall.optin.carDelivery = data.carDelivery
      ? 1
      : 2
    objDataLayer = { ...objDataLayer, ...newObjDataLayer }

    setLeadInfo(leadDateInfo)

    handleFormCookie(
      {
        fullName: lead.Nome,
        email: lead.Email,
        numberPhone: lead.Telefone,
        message: 'Olá, tenho interesse no veículo. Por favor entre em contato.',
        newsletter: lead.QueroReceberPesquisa,
      },
      isActive
    )

    setLoading(true)

    leadAPI
      .create(lead)
      .then(() => {
        setLoading(false)
        closeModal()
        sendDispatchEvent('formVideoCallLead')
        openModal()
      })
      .catch(err => {
        setLoading(false)
        console.error(err)
      })
  }

  const handleCloseModal = () => {
    closeModal()
    const { href, search } = window.location

    const trackingData = createTrackingData(
      'customPageView',
      DATALAYER_PAGES.adDetail,
      {
        page: {
          url: href,
          trackingCode: {
            idcmp: getQueryParameter(search, 'idcmp'),
            idcmpint: getQueryParameter(search, 'idcmpint'),
            icid: getQueryParameter(search, 'icid'),
            lkid: getQueryParameter(search, 'lkid'),
          },
        },
      }
    )

    dispatchAnalyticsEvent(trackingData)
  }

  const dataModalTest = 'modal_videochamada'
  const dataModalTestId = `${dataModalTest}_${ad.UniqueId}`
  const dataButtonTest = 'button_continue_videochamada'

  return (
    <ModalContainer
      show={modal}
      useWhiteBox
      onBgClick={() => handleCloseModal()}
    >
      <VideoChamadaModal
        data-test-component={dataModalTest}
        data-test-id={dataModalTestId}
      >
        <FlexContainer>
          <MainText>Informe os seus dados para agendar a videochamada</MainText>
          <IconButton onClick={() => handleCloseModal()}>
            <Icon name="Close_1" fill="#2E2E37" size="20" />
          </IconButton>
        </FlexContainer>

        <Form {...formHandles} setContinueIsActive={setIsActiveForm} />

        <Calendar
          setContinueIsActive={setisActiveCalendar}
          dayActive={dayActive}
          setDayActive={setDayActive}
          allDays={allDays}
          setHourActive={setHourActive}
          setContinueHourIsActive={setIsActiveHours}
        />

        <Hours
          setContinueIsActive={setIsActiveHours}
          dayActive={allDays[dayActive]}
          hourActive={hourActive}
          setHourActive={setHourActive}
        />

        <FlexContainer>
          <FormInfo>
            Agende a Videochamada com 2 horas de antecedência.
          </FormInfo>
          <Button
            data-qa="btn_video_call_continue"
            data-test-component={dataButtonTest}
            disabled={
              !isActiveForm || !isActiveCalendar || !isActiveHours || loading
            }
            type="button"
            onClick={formHandles.handleSubmit(submitForm)}
          >
            {loading ? 'Aguarde' : 'Continuar'}
          </Button>
        </FlexContainer>
      </VideoChamadaModal>
    </ModalContainer>
  )
}
